import socketService from './socket.service';

interface Group {
  id: string;
  name: string;
  description?: string;
  avatar?: string;
  type: 'private' | 'public' | 'channel';
  ownerId: string;
  memberCount: number;
  maxMembers: number;
  isMuted: boolean;
  createdAt: Date;
  members?: GroupMember[];
}

interface GroupMember {
  id: string;
  groupId: string;
  userId: string;
  user?: {
    id: string;
    nickname: string;
    avatar: string;
    status: 'online' | 'offline' | 'busy';
  };
  role: 'owner' | 'admin' | 'member';
  groupNickname?: string;
  isMuted: boolean;
  joinedAt: Date;
}

export class GroupService {
  private groups: Group[] = [];
  private currentGroup: Group | null = null;

  async createGroup(data: {
    name: string;
    description?: string;
    avatar?: string;
    type?: Group['type'];
    maxMembers?: number;
  }): Promise<Group> {
    return new Promise((resolve, reject) => {
      socketService.emit('createGroup', data);

      const handleGroupCreated = (group: Group) => {
        this.groups.push(group);
        resolve(group);
      };

      socketService.on('group:created', handleGroupCreated);
      socketService.on('error', reject);
    });
  }

  async loadGroups(): Promise<Group[]> {
    return new Promise((resolve, reject) => {
      socketService.emit('getGroups');

      const handleGroups = (groups: Group[]) => {
        this.groups = groups;
        resolve(groups);
      };

      socketService.on('groups:list', handleGroups);
      socketService.on('error', reject);
    });
  }

  async loadGroup(groupId: string): Promise<Group> {
    return new Promise((resolve, reject) => {
      socketService.emit('getGroup', { groupId });

      const handleGroup = (group: Group) => {
        this.currentGroup = group;
        resolve(group);
      };

      socketService.on('group:detail', handleGroup);
      socketService.on('error', reject);
    });
  }

  async loadMembers(groupId: string): Promise<GroupMember[]> {
    return new Promise((resolve, reject) => {
      socketService.emit('getGroupMembers', { groupId });

      const handleMembers = (members: GroupMember[]) => {
        if (this.currentGroup) {
          this.currentGroup.members = members;
        }
        resolve(members);
      };

      socketService.on('group:members', handleMembers);
      socketService.on('error', reject);
    });
  }

  async addMember(groupId: string, userId: string): Promise<GroupMember> {
    return new Promise((resolve, reject) => {
      socketService.emit('addGroupMember', { groupId, userId });

      const handleMemberAdded = (member: GroupMember) => {
        if (this.currentGroup && this.currentGroup.members) {
          this.currentGroup.members.push(member);
          this.currentGroup.memberCount++;
        }
        resolve(member);
      };

      socketService.on('group:memberAdded', handleMemberAdded);
      socketService.on('error', reject);
    });
  }

  async removeMember(groupId: string, userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      socketService.emit('removeGroupMember', { groupId, userId });

      const handleMemberRemoved = ({ userId }: { groupId: string; userId: string }) => {
        if (this.currentGroup && this.currentGroup.members) {
          this.currentGroup.members = this.currentGroup.members.filter((m) => m.userId !== userId);
          this.currentGroup.memberCount--;
        }
        resolve();
      };

      socketService.on('group:memberRemoved', handleMemberRemoved);
      socketService.on('error', reject);
    });
  }

  async updateMemberRole(
    groupId: string,
    userId: string,
    role: GroupMember['role'],
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      socketService.emit('updateGroupMember', { groupId, userId, role });

      const handleRoleUpdated = ({ role }: any) => {
        if (this.currentGroup && this.currentGroup.members) {
          const member = this.currentGroup.members.find((m) => m.userId === userId);
          if (member) {
            member.role = role;
          }
        }
        resolve();
      };

      socketService.on('group:roleUpdated', handleRoleUpdated);
      socketService.on('error', reject);
    });
  }

  async muteMember(groupId: string, userId: string, isMuted: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      socketService.emit('muteGroupMember', { groupId, userId, isMuted });

      const handleMuted = ({ userId, isMuted }: any) => {
        if (this.currentGroup && this.currentGroup.members) {
          const member = this.currentGroup.members.find((m) => m.userId === userId);
          if (member) {
            member.isMuted = isMuted;
          }
        }
        resolve();
      };

      socketService.on('group:memberMuted', handleMuted);
      socketService.on('error', reject);
    });
  }

  async updateGroup(
    groupId: string,
    data: {
      name?: string;
      description?: string;
      avatar?: string;
    },
  ): Promise<Group> {
    return new Promise((resolve, reject) => {
      socketService.emit('updateGroup', { groupId, ...data });

      const handleGroupUpdated = (group: Group) => {
        if (this.currentGroup && this.currentGroup.id === groupId) {
          Object.assign(this.currentGroup, data);
        }
        resolve(group);
      };

      socketService.on('group:updated', handleGroupUpdated);
      socketService.on('error', reject);
    });
  }

  async disbandGroup(groupId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      socketService.emit('disbandGroup', { groupId });

      const handleGroupDisbanded = ({ groupId }: { groupId: string }) => {
        this.groups = this.groups.filter((g) => g.id !== groupId);
        if (this.currentGroup && this.currentGroup.id === groupId) {
          this.currentGroup = null;
        }
        resolve();
      };

      socketService.on('group:disbanded', handleGroupDisbanded);
      socketService.on('error', reject);
    });
  }

  getAll(): Group[] {
    return this.groups;
  }

  getCurrent(): Group | null {
    return this.currentGroup;
  }

  setCurrent(group: Group): void {
    this.currentGroup = group;
  }

  clearCurrent(): void {
    this.currentGroup = null;
  }
}

export default new GroupService();
